import { useMemo } from "react";

import useAssistantPanelAnalytics from "components/AssistantPanel/analytics/useAnalytics";
import useAssistantPanelAvailable from "hooks/useAssistantPanelAvailable";
import useAssistantPanelSendPrompt from "components/AssistantPanel/hooks/useSendPrompt";
import { LogoInfraAssistantColored } from "components/icons/generated";

import useCommandPaletteStore from "../store/useCommandPaletteStore";
import { detectSearchIntent } from "../utils/detectSearchIntent";
import { CommandPaletteAction, CommandPaletteActionType } from "../types";
import { BUCKET_IDS } from "../constants";

const ACTION_TITLE = "Ask Infra Assistant";

const useCommandPaletteAskInfraAssistantAction = (): CommandPaletteAction | undefined => {
  const searchQuery = useCommandPaletteStore((state) => state.searchQuery);
  const isAskAvailable = useAssistantPanelAvailable();
  const { openPrompt, isChatOpen } = useAssistantPanelSendPrompt();
  const { trackConversationOpened } = useAssistantPanelAnalytics();

  return useMemo(() => {
    if (!isAskAvailable) {
      return undefined;
    }

    const question = detectSearchIntent({ title: ACTION_TITLE }, searchQuery) ? searchQuery : "";

    return {
      type: CommandPaletteActionType.SIMPLE,
      id: "ask-infra-assistant",
      title: ACTION_TITLE,
      alwaysVisible: true,
      bucketId: BUCKET_IDS.ASK_INFRA_ASSISTANT,
      icon: LogoInfraAssistantColored,
      metadata: { title: question || undefined },
      onAction: () => {
        trackConversationOpened("command_palette");

        openPrompt(question, { newChat: !isChatOpen });
      },
    };
  }, [searchQuery, isAskAvailable, openPrompt, isChatOpen, trackConversationOpened]);
};

export default useCommandPaletteAskInfraAssistantAction;
